<template>
  <div v-if="memberCountries.length">
    <label
      :for="disabled ? undefined : id"
      class="mb-1 block text-xs text-gray-600"
    >
      {{ name }}
      <sup
        v-if="required"
        class="top-0 text-sm font-bold"
        :class="disabled ? 'text-gray-600' : 'text-red-400'"
      >
        *
      </sup>
    </label>

    <div class="flex">
      <div class="relative w-20">
        <select
          id="form_phoneCode"
          v-model="phoneCode"
          :class="[className, phoneCode ? 'text-transparent' : 'text-gray-400']"
          class="block w-full cursor-pointer appearance-none rounded-l border border-r-0 border-solid bg-gray-100 py-[5px] pl-2 pr-7 text-sm [&>option:disabled]:text-gray-400 [&>option]:text-black"
          @focus="handleFocus"
          @blur="handleCodeBlur"
          @change="handleCodeChange"
        >
          <option value="" disabled>
            {{ $t('form.select') }}
          </option>
          <option
            v-for="item in memberCountries"
            :key="item.code"
            :value="item.code"
          >
            {{ item.name }} +{{ item.code }}
          </option>
        </select>
        <IconCaret
          direction="down"
          class="pointer-events-none absolute right-1 top-1 h-6 w-6 text-gray-600"
          :class="disabled && 'opacity-40'"
        />
        <div
          class="pointer-events-none absolute inset-0 flex items-center pl-2"
        >
          <span v-if="phoneCode" class="text-sm">+{{ phoneCode }}</span>
        </div>
      </div>

      <div class="flex-1">
        <input
          :id="id"
          v-model.trim="phoneNumber"
          :type="type"
          :disabled="disabled"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :class="className"
          class="block h-8 w-full rounded-r border border-solid bg-gray-100 px-2 py-[5px] text-sm"
          @focus="handleFocus"
          @blur="handleNumberBlur"
          @input="handleNumberChange"
        />
      </div>
    </div>

    <span v-show="isError" class="mt-1 block px-2 text-xs text-red-400">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';
  import type { InputHTMLAttributes } from 'vue';

  interface IProps {
    id: string;
    name: string;
    type?: InputHTMLAttributes['type'];
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    maxlength?: number;
    isError?: boolean;
    errorMessage?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    type: 'text',
    required: false,
    disabled: false,
    placeholder: '',
    maxlength: 100,
    isError: false,
    errorMessage: '',
  });

  const emits = defineEmits<{
    (e: 'code-blur', value: string): void;
    (e: 'code-change', value: string): void;
    (e: 'number-blur', value: string): void;
    (e: 'number-change', value: string): void;
  }>();

  const phoneCode = defineModel<string>('phoneCode', { default: '' });
  const phoneNumber = defineModel<string>('phoneNumber', { default: '' });

  const mainStore = useMainStore();
  const { memberCountries } = storeToRefs(mainStore);

  const isFocus = ref<boolean>(false);

  const className = computed((): string => {
    if (props.disabled) {
      return 'pointer-events-none cursor-not-allowed opacity-40';
    }
    if (props.isError) {
      return 'border-red-400';
    }
    if (isFocus.value) {
      return 'border-blue-400';
    }
    return 'border-gray-600';
  });

  const handleFocus = (): void => {
    isFocus.value = true;
  };

  const handleCodeBlur = (): void => {
    isFocus.value = false;
    emits('code-blur', phoneCode.value);
  };

  const handleCodeChange = (): void => {
    emits('code-change', phoneCode.value);
  };

  const handleNumberBlur = (): void => {
    isFocus.value = false;
    emits('number-blur', phoneNumber.value);
  };

  const handleNumberChange = (): void => {
    emits('number-change', phoneNumber.value);
  };
</script>
